import {addQueryToPath} from '../../helpers/browser/urlHelpers';
import {ChannelModel} from '../api/channelModel';
import {InboxModel} from '../api/inboxModel';

export interface InboxWithChannelsModel {
  inbox: InboxModel;
  channels: ReadonlyArray<ChannelModel>;
}

/*
 * Search URL.
 */

export enum InboxSearchSortOrdersEnum {
  NAME_ASC = 'name',
  NAME_DESC = '-name',
  PUBLIC_ASC = 'public',
  PUBLIC_DESC = '-public',
}

export interface SearchInboxOptions {
  /** Only list inboxes after the offset. */
  offset?: number;
  /** Limits the number of inboxes returned in the response. */
  limit?: number;
  /** Whether to include private inboxes. */
  shouldIncludePrivate?: boolean;
  /** Search inboxes in a specific namespace */
  namespace?: string;
  /** Returns channels alongside inboxes. */
  shouldIncludeChannels?: boolean;
  /** Sort inboxes by field */
  sortOrder?: InboxSearchSortOrdersEnum;
  /** Include all shared inboxes, even those that the user can not access. Default value: true */
  shouldIncludeInaccessible?: boolean;
}

export function buildInboxSearchUrl(companyUrl: string, query: string, options: SearchInboxOptions) {
  if (options.namespace) {
    return addQueryToPath(
      `${companyUrl}/namespaces/${options.namespace}/search_inbox/${encodeURIComponent(query.toLowerCase())}`,
      serializeSearchInboxOptions(options),
    );
  }

  return addQueryToPath(
    `${companyUrl}/search_inbox/${encodeURIComponent(query.toLowerCase())}`,
    serializeSearchInboxOptions(options),
  );
}

function serializeSearchInboxOptions(options: SearchInboxOptions) {
  const {
    offset = 0,
    limit = 50,
    shouldIncludePrivate = false,
    shouldIncludeChannels = false,
    shouldIncludeInaccessible = true,
    sortOrder,
  } = options;

  return {
    offset,
    limit,
    include_private: shouldIncludePrivate,
    with_channels: shouldIncludeChannels,
    exclude_inaccessible: !shouldIncludeInaccessible,
    sort: sortOrder,
  };
}

/** Check if the specified inbox has a color. */

export function inboxHasColor(inbox: Pick<InboxModel, 'color'>) {
  return inbox.color.toLowerCase() !== '#ffffff';
}

/** Predicate that tests whether an inbox name contains the specified string. */

export function doesInboxMatchQuery(inbox: InboxModel, query: string) {
  const lowerCaseQuery = query.toLowerCase();

  // Match by name.
  const doesMatchByName = inbox.name.toLowerCase().includes(lowerCaseQuery);

  // Match by alias.
  const doesMatchByAlias = inbox.alias.toLowerCase().includes(lowerCaseQuery);

  return doesMatchByAlias || doesMatchByName;
}

export const inboxQueryKeyFactory = {
  all: ['inboxes'] as const,
  id: (id: number) => [...inboxQueryKeyFactory.all, {id}],
  byIds: (ids: ReadonlyArray<number>) => [...inboxQueryKeyFactory.all, 'ids', {ids}] as const,
  listBase: ['inboxes', 'list'] as const,
  list: (query: string, filters: SearchInboxOptions) => [
    ...inboxQueryKeyFactory.listBase,
    {...filters, query},
  ],
  team: (namespace: string) => [...inboxQueryKeyFactory.all, 'all', {namespace}] as const,
  workflows: (inboxId: number) => [...inboxQueryKeyFactory.all, {id: inboxId, type: 'workflows'}],
};
